/**
 * PostCheckoutPage - Écran post-clôture avec statuts de synchronisation
 * Requirements: 12.1, 12.2, 12.3, 12.4, 12.5, 12.6, 12.7
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  DocumentArrowDownIcon,
  ArrowLeftIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { checkoutService } from '../services/checkoutService';
import { SyncStatus, CNSSyncStatus, ONTArchiveStatus, FPTStatus } from '../types/checkout';

/**
 * Format date/time in French locale
 */
const formatDateTime = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

/**
 * Get status color class based on sync status
 * Green = OK (SENT/ARCHIVED/INCLUDED_IN_MONTHLY_BASE)
 * Orange = Pending (PENDING)
 * Red = Error (FAILED)
 */
const getCNSStatusColor = (status: CNSSyncStatus): string => {
  switch (status) {
    case 'SENT':
      return 'bg-green-100 text-green-800';
    case 'PENDING':
      return 'bg-orange-100 text-orange-800';
    case 'FAILED':
      return 'bg-red-100 text-red-800';
    case 'NOT_SENT':
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getONTStatusColor = (status: ONTArchiveStatus): string => {
  switch (status) {
    case 'ARCHIVED':
      return 'bg-green-100 text-green-800';
    case 'PENDING':
      return 'bg-orange-100 text-orange-800';
    case 'FAILED':
      return 'bg-red-100 text-red-800';
    case 'NOT_ARCHIVED':
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getFPTStatusColor = (status: FPTStatus): string => {
  switch (status) {
    case 'INCLUDED_IN_MONTHLY_BASE':
      return 'bg-green-100 text-green-800';
    case 'ELIGIBLE':
      return 'bg-orange-100 text-orange-800';
    case 'NOT_ELIGIBLE':
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Get human-readable status label
 */
const getCNSStatusLabel = (status: CNSSyncStatus): string => {
  switch (status) {
    case 'SENT':
      return 'Synchronisé';
    case 'PENDING':
      return 'En attente';
    case 'FAILED':
      return 'Échec';
    case 'NOT_SENT':
    default:
      return 'Non envoyé';
  }
};

const getONTStatusLabel = (status: ONTArchiveStatus): string => {
  switch (status) {
    case 'ARCHIVED':
      return 'Archivé';
    case 'PENDING':
      return 'En attente';
    case 'FAILED':
      return 'Échec';
    case 'NOT_ARCHIVED':
    default:
      return 'Non archivé';
  }
};

const getFPTStatusLabel = (status: FPTStatus): string => {
  switch (status) {
    case 'INCLUDED_IN_MONTHLY_BASE':
      return 'Inclus dans base mensuelle';
    case 'ELIGIBLE':
      return 'Éligible';
    case 'NOT_ELIGIBLE':
    default:
      return 'Non éligible';
  }
};

export default function PostCheckoutPage() {
  const { checkoutId } = useParams<{ checkoutId: string }>();
  const navigate = useNavigate();

  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryingCNS, setRetryingCNS] = useState(false);
  const [retryingONT, setRetryingONT] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadSyncStatus = useCallback(async () => {
    if (!checkoutId) return;
    try {
      setLoading(true);
      setError(null);
      const status = await checkoutService.getSyncStatus(checkoutId);
      setSyncStatus(status);
    } catch (err: any) {
      console.error('Error loading sync status:', err);
      setError(err?.message || 'Erreur lors du chargement des statuts');
    } finally {
      setLoading(false);
    }
  }, [checkoutId]);

  useEffect(() => {
    loadSyncStatus();
  }, [loadSyncStatus]);

  const handleRetryCNS = async () => {
    if (!checkoutId) return;
    try {
      setRetryingCNS(true);
      await checkoutService.retrySync(checkoutId, 'cns');
      await loadSyncStatus();
    } catch (err: any) {
      console.error('Error retrying CNS sync:', err);
      setError(err?.message || 'Erreur lors de la relance CNS');
    } finally {
      setRetryingCNS(false);
    }
  };

  const handleRetryONT = async () => {
    if (!checkoutId) return;
    try {
      setRetryingONT(true);
      await checkoutService.retrySync(checkoutId, 'ont');
      await loadSyncStatus();
    } catch (err: any) {
      console.error('Error retrying ONT sync:', err);
      setError(err?.message || 'Erreur lors de la relance ONT');
    } finally {
      setRetryingONT(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!checkoutId) return;
    try {
      setDownloading(true);
      const blob = await checkoutService.downloadCheckoutPDF(checkoutId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `checkout-${syncStatus?.checkoutReference || checkoutId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading PDF:', err);
      setError(err?.message || 'Erreur lors du téléchargement du PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error && !syncStatus) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow p-6 max-w-md">
          <div className="flex items-center text-red-600 mb-4">
            <ExclamationTriangleIcon className="h-6 w-6 mr-2" />
            <span className="font-medium">Erreur</span>
          </div>
          <p className="text-gray-700 mb-4">{error}</p>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/checkout')}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Retour à la liste
            </button>
            <button
              onClick={loadSyncStatus}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!syncStatus) return null;

  const { cns, ont, fpt } = syncStatus;

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate('/checkout')}
          className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Retour à la liste des check-outs
        </button>

        {/* Success Banner - Requirement 12.1 */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
          <div className="flex items-center">
            <CheckCircleIcon className="h-12 w-12 text-green-500 mr-4" />
            <div>
              <h1 className="text-2xl font-bold text-green-800">Check-out clôturé</h1>
              <p className="text-green-700 mt-1">
                Le dossier a été clôturé avec succès et verrouillé.
              </p>
            </div>
          </div>
        </div>

        {/* Error message if any */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Checkout Reference Card - Requirements 12.2, 12.3 */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Informations de clôture</h2>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Checkout Reference - Requirement 12.2 */}
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Référence de clôture
                </label>
                <p className="mt-1 text-lg font-mono font-semibold text-purple-600">
                  {syncStatus.checkoutReference}
                </p>
              </div>
              {/* Timestamp - Requirement 12.3 */}
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Horodatage de clôture
                </label>
                <p className="mt-1 text-sm text-gray-900">
                  {formatDateTime(cns.lastAttempt || ont.lastAttempt)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sync Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* CNS Sync Status - Requirement 12.4 */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900">Synchronisation CNS</h3>
            </div>
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-3">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getCNSStatusColor(cns.status)}`}
                >
                  {getCNSStatusLabel(cns.status)}
                </span>
              </div>
              {cns.lastAttempt && (
                <div className="flex items-center text-xs text-gray-500 mb-2">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  Dernière tentative: {formatDateTime(cns.lastAttempt)}
                </div>
              )}
              {cns.attempts > 0 && (
                <p className="text-xs text-gray-500 mb-2">
                  Tentatives: {cns.attempts}
                </p>
              )}
              {cns.status === 'FAILED' && cns.errorMessage && (
                <div className="bg-red-50 rounded p-2 mb-3">
                  <p className="text-xs text-red-700">{cns.errorMessage}</p>
                </div>
              )}
              {cns.status === 'FAILED' && (
                <button
                  onClick={handleRetryCNS}
                  disabled={retryingCNS}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  {retryingCNS ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                      Relance...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-1" />
                      Relancer
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* ONT Archive Status - Requirement 12.5 */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900">Archivage ONT</h3>
            </div>
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-3">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getONTStatusColor(ont.status)}`}
                >
                  {getONTStatusLabel(ont.status)}
                </span>
              </div>
              {ont.lastAttempt && (
                <div className="flex items-center text-xs text-gray-500 mb-2">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  Dernière tentative: {formatDateTime(ont.lastAttempt)}
                </div>
              )}
              {ont.attempts > 0 && (
                <p className="text-xs text-gray-500 mb-2">
                  Tentatives: {ont.attempts}
                </p>
              )}
              {ont.archiveReference && (
                <p className="text-xs text-gray-500 mb-2">
                  Réf. archive: {ont.archiveReference}
                </p>
              )}
              {ont.status === 'FAILED' && ont.errorMessage && (
                <div className="bg-red-50 rounded p-2 mb-3">
                  <p className="text-xs text-red-700">{ont.errorMessage}</p>
                </div>
              )}
              {ont.status === 'FAILED' && (
                <button
                  onClick={handleRetryONT}
                  disabled={retryingONT}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  {retryingONT ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                      Relance...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="h-4 w-4 mr-1" />
                      Relancer
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* FPT Status - Requirement 12.6 */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900">Statut FPT</h3>
            </div>
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-3">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getFPTStatusColor(fpt.status)}`}
                >
                  {getFPTStatusLabel(fpt.status)}
                </span>
              </div>
              {fpt.reportingPeriod && (
                <p className="text-xs text-gray-500">
                  Période de reporting: {fpt.reportingPeriod}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Actions - Requirement 12.7 */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Actions</h2>
          </div>
          <div className="px-6 py-4">
            <div className="flex flex-wrap gap-4">
              <button
                onClick={handleDownloadPDF}
                disabled={downloading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
              >
                {downloading ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                    Téléchargement...
                  </>
                ) : (
                  <>
                    <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                    Télécharger résumé PDF
                  </>
                )}
              </button>
              <button
                onClick={loadSyncStatus}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowPathIcon className="h-5 w-5 mr-2" />
                Actualiser les statuts
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
